import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Plus,
  Eye,
  EyeOff,
  Trash2,
  Pencil,
  Check,
  X,
  MoveHorizontal,
  Grid2X2,
  Hash,
  ChevronDown,
  FileText,
  ArrowUpRight,
  MousePointer2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useCreateTakeoffGroup,
  useUpdateTakeoffGroup,
  useDeleteTakeoffGroup,
  type TakeoffGroup,
} from "@/hooks/useTakeoffGroups";
import {
  aggregateForGroup,
  aggregateByDocument,
  type RawMeasurement,
  type DrilldownAnnotation,
} from "@/lib/takeoffAggregation";
import { linearUnitLabel, areaUnitLabel } from "./takeoff-helpers";
import type { TakeoffOwnerType } from "@/hooks/useDocumentAnnotations";

const GROUP_COLORS = [
  "#3B82F6", "#EF4444", "#22C55E", "#F59E0B",
  "#8B5CF6", "#EC4899", "#06B6D4", "#F97316",
];

interface TakeoffGroupsPanelProps {
  ownerType: TakeoffOwnerType;
  ownerId: string;
  groups: TakeoffGroup[];
  rawAnnotations: RawMeasurement[];
  drilldownAnnotations: DrilldownAnnotation[];
  displayUnit: string;
  activeGroupId: string | null;
  onActiveGroupChange: (id: string | null) => void;
  hiddenGroupIds: Set<string>;
  onToggleGroupVisibility: (id: string) => void;
  documentMap: Record<string, string>;
  onAnnotationClick: (params: { docId: string; pageNumber: number; annotationId: string }) => void;
  readOnly?: boolean;
}

function annotationTypeIcon(type: string) {
  switch (type) {
    case "measurement_linear":
      return <MoveHorizontal className="h-3 w-3" />;
    case "measurement_area":
      return <Grid2X2 className="h-3 w-3" />;
    case "measurement_count":
      return <Hash className="h-3 w-3" />;
    default:
      return <MousePointer2 className="h-3 w-3" />;
  }
}

export const TakeoffGroupsPanel = ({
  ownerType,
  ownerId,
  groups,
  rawAnnotations,
  drilldownAnnotations,
  displayUnit,
  activeGroupId,
  onActiveGroupChange,
  hiddenGroupIds,
  onToggleGroupVisibility,
  documentMap,
  onAnnotationClick,
  readOnly = false,
}: TakeoffGroupsPanelProps) => {
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupColor, setNewGroupColor] = useState(GROUP_COLORS[0]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");

  const createGroup = useCreateTakeoffGroup();
  const updateGroup = useUpdateTakeoffGroup();
  const deleteGroup = useDeleteTakeoffGroup();

  const handleCreate = () => {
    if (!newGroupName.trim()) return;
    createGroup.mutate(
      { ownerType, ownerId, name: newGroupName.trim(), color: newGroupColor },
      {
        onSuccess: () => {
          setNewGroupName("");
          setShowNewForm(false);
        },
      }
    );
  };

  const handleUpdate = (id: string) => {
    if (!editName.trim()) return;
    updateGroup.mutate(
      { id, ownerType, ownerId, name: editName.trim(), color: editColor },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleDelete = (id: string) => {
    if (activeGroupId === id) onActiveGroupChange(null);
    deleteGroup.mutate({ id, ownerType, ownerId });
  };

  const startEdit = (g: TakeoffGroup) => {
    setEditingId(g.id);
    setEditName(g.name);
    setEditColor(g.color);
  };

  // Unassigned annotations
  const unassigned = aggregateForGroup(rawAnnotations, null, displayUnit);
  const unassignedCount = rawAnnotations.filter((a) => !a.takeoff_group_id).length;
  const unassignedDrilldown = useMemo(
    () => drilldownAnnotations.filter((a) => !a.takeoff_group_id),
    [drilldownAnnotations]
  );

  const renderTotals = (totals: { totalLinear: number; totalArea: number; totalCount: number }) => (
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
      {totals.totalLinear > 0 && (
        <span className="flex items-center gap-0.5">
          <MoveHorizontal className="h-2.5 w-2.5" />{totals.totalLinear.toFixed(1)}
        </span>
      )}
      {totals.totalArea > 0 && (
        <span className="flex items-center gap-0.5">
          <Grid2X2 className="h-2.5 w-2.5" />{totals.totalArea.toFixed(1)}
        </span>
      )}
      {totals.totalCount > 0 && (
        <span className="flex items-center gap-0.5">
          <Hash className="h-2.5 w-2.5" />{totals.totalCount}
        </span>
      )}
    </div>
  );

  return (
    <div className="w-[220px] shrink-0 border-l border-border flex flex-col bg-card">
      <div className="px-3 py-2.5 border-b border-border">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          Groups
        </h3>
      </div>

      {/* Active group selector */}
      {!readOnly && (
        <div className="px-3 py-2 border-b border-border">
          <p className="text-[10px] text-muted-foreground mb-1">Active group (new annotations)</p>
          <select
            className="w-full h-7 text-xs rounded-md border border-border bg-background px-2"
            value={activeGroupId ?? ""}
            onChange={(e) => onActiveGroupChange(e.target.value || null)}
          >
            <option value="">Unassigned</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-2 space-y-1">
          {/* Groups */}
          {groups.map((g) => {
            const totals = aggregateForGroup(rawAnnotations, g.id, displayUnit);
            const count = rawAnnotations.filter((a) => a.takeoff_group_id === g.id).length;
            const isHidden = hiddenGroupIds.has(g.id);
            const isEditing = editingId === g.id;

            if (isEditing && !readOnly) {
              return (
                <div key={g.id} className="p-2 border rounded-md space-y-2 bg-muted/30">
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="h-7 text-xs"
                    autoFocus
                  />
                  <div className="flex gap-1">
                    {GROUP_COLORS.map((c) => (
                      <button
                        key={c}
                        className={cn("h-5 w-5 rounded-full border-2", editColor === c ? "border-foreground" : "border-transparent")}
                        style={{ backgroundColor: c }}
                        onClick={() => setEditColor(c)}
                      />
                    ))}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => handleUpdate(g.id)}>
                      <Check className="h-3 w-3 mr-0.5" />Save
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setEditingId(null)}>
                      <X className="h-3 w-3 mr-0.5" />Cancel
                    </Button>
                  </div>
                </div>
              );
            }

            const groupDrilldown = drilldownAnnotations.filter((a) => a.takeoff_group_id === g.id);
            const docsTouched = new Set(groupDrilldown.map((a) => a.document_id)).size;

            return (
              <GroupRowWithDrilldown
                key={g.id}
                group={g}
                totals={totals}
                count={count}
                docsTouched={docsTouched}
                isHidden={isHidden}
                isActive={activeGroupId === g.id}
                readOnly={readOnly}
                ownerType={ownerType}
                displayUnit={displayUnit}
                drilldownAnnotations={groupDrilldown}
                documentMap={documentMap}
                onActiveGroupChange={onActiveGroupChange}
                onToggleGroupVisibility={onToggleGroupVisibility}
                onStartEdit={() => startEdit(g)}
                onDelete={() => handleDelete(g.id)}
                onAnnotationClick={onAnnotationClick}
                renderTotals={renderTotals}
              />
            );
          })}

          {/* Unassigned bucket */}
          <UnassignedRow
            totals={unassigned}
            count={unassignedCount}
            isActive={activeGroupId === null}
            isHidden={hiddenGroupIds.has("__unassigned__")}
            readOnly={readOnly}
            ownerType={ownerType}
            displayUnit={displayUnit}
            drilldownAnnotations={unassignedDrilldown}
            documentMap={documentMap}
            onActiveGroupChange={onActiveGroupChange}
            onToggleGroupVisibility={onToggleGroupVisibility}
            onAnnotationClick={onAnnotationClick}
            renderTotals={renderTotals}
          />
        </div>
      </ScrollArea>

      {/* New Group */}
      {!readOnly && (
        <div className="border-t border-border p-2">
          {showNewForm ? (
            <div className="space-y-2">
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Group name…"
                className="h-7 text-xs"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              />
              <div className="flex gap-1">
                {GROUP_COLORS.map((c) => (
                  <button
                    key={c}
                    className={cn("h-4 w-4 rounded-full border-2", newGroupColor === c ? "border-foreground" : "border-transparent")}
                    style={{ backgroundColor: c }}
                    onClick={() => setNewGroupColor(c)}
                  />
                ))}
              </div>
              <div className="flex gap-1">
                <Button size="sm" className="h-6 text-xs flex-1" onClick={handleCreate} disabled={createGroup.isPending}>
                  Create
                </Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowNewForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => setShowNewForm(true)}>
              <Plus className="h-3 w-3 mr-1" /> New Group
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Group Row with expandable drilldown ─── */

function GroupRowWithDrilldown({
  group,
  totals,
  count,
  docsTouched,
  isHidden,
  isActive,
  readOnly,
  ownerType,
  displayUnit,
  drilldownAnnotations,
  documentMap,
  onActiveGroupChange,
  onToggleGroupVisibility,
  onStartEdit,
  onDelete,
  onAnnotationClick,
  renderTotals,
}: {
  group: TakeoffGroup;
  totals: { totalLinear: number; totalArea: number; totalCount: number };
  count: number;
  docsTouched: number;
  isHidden: boolean;
  isActive: boolean;
  readOnly: boolean;
  ownerType: TakeoffOwnerType;
  displayUnit: string;
  drilldownAnnotations: DrilldownAnnotation[];
  documentMap: Record<string, string>;
  onActiveGroupChange: (id: string | null) => void;
  onToggleGroupVisibility: (id: string) => void;
  onStartEdit: () => void;
  onDelete: () => void;
  onAnnotationClick: (params: { docId: string; pageNumber: number; annotationId: string }) => void;
  renderTotals: (t: { totalLinear: number; totalArea: number; totalCount: number }) => React.ReactNode;
}) {
  const byDoc = useMemo(
    () => aggregateByDocument(drilldownAnnotations, displayUnit, documentMap),
    [drilldownAnnotations, displayUnit, documentMap]
  );

  const hasLineage = ownerType === "project" && !!group.source_estimate_group_id;

  return (
    <Collapsible>
      <div
        className={cn(
          "rounded-md hover:bg-muted/50 cursor-pointer group",
          isActive && "bg-primary/5 border border-primary/20"
        )}
      >
        <div
          className="flex items-start gap-2 p-2"
          onClick={() => !readOnly && onActiveGroupChange(group.id)}
        >
          <div className="h-3.5 w-3.5 rounded-full shrink-0 mt-0.5" style={{ backgroundColor: group.color }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium truncate">{group.name}</span>
              {count > 0 && (
                <Badge variant="secondary" className="text-[9px] px-1 py-0">{count}</Badge>
              )}
              {hasLineage && (
                <Badge variant="outline" className="text-[8px] px-1 py-0 border-blue-300 text-blue-600">
                  Est
                </Badge>
              )}
            </div>
            {renderTotals(totals)}
            {count > 0 && (
              <div className="text-[10px] text-muted-foreground mt-0.5">
                {docsTouched} drawing{docsTouched !== 1 ? "s" : ""}
              </div>
            )}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {count > 0 && (
              <CollapsibleTrigger asChild>
                <button
                  className="p-0.5 rounded hover:bg-muted"
                  onClick={(e) => e.stopPropagation()}
                  title="Expand details"
                >
                  <ChevronDown className="h-3 w-3 text-muted-foreground transition-transform" />
                </button>
              </CollapsibleTrigger>
            )}
            <button
              className="p-0.5 rounded hover:bg-muted opacity-0 group-hover:opacity-100"
              onClick={(e) => { e.stopPropagation(); onToggleGroupVisibility(group.id); }}
              title={isHidden ? "Show" : "Hide"}
            >
              {isHidden ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
            </button>
            {!readOnly && (
              <>
                <button
                  className="p-0.5 rounded hover:bg-muted opacity-0 group-hover:opacity-100"
                  onClick={(e) => { e.stopPropagation(); onStartEdit(); }}
                >
                  <Pencil className="h-3 w-3 text-muted-foreground" />
                </button>
                <button
                  className="p-0.5 rounded hover:bg-destructive/10 opacity-0 group-hover:opacity-100"
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                >
                  <Trash2 className="h-3 w-3 text-destructive" />
                </button>
              </>
            )}
          </div>
        </div>

        <CollapsibleContent>
          <div className="px-2 pb-2 space-y-1 border-t border-border/50 pt-1.5 ml-5">
            {byDoc.map((docSub) => (
              <Collapsible key={docSub.documentId}>
                <CollapsibleTrigger className="flex items-center gap-1.5 w-full px-1 py-1 rounded hover:bg-muted/30 text-left">
                  <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-[11px] truncate flex-1">{docSub.documentName}</span>
                  <Badge variant="secondary" className="text-[9px] px-1 py-0">{docSub.annotationCount}</Badge>
                  <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-0.5">
                  {docSub.annotations.map((ann) => (
                    <DrilldownAnnotationRow
                      key={ann.id}
                      ann={ann}
                      ownerType={ownerType}
                      onAnnotationClick={onAnnotationClick}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* ─── Unassigned bucket with drilldown ─── */

function UnassignedRow({
  totals,
  count,
  isActive,
  isHidden,
  readOnly,
  ownerType,
  displayUnit,
  drilldownAnnotations,
  documentMap,
  onActiveGroupChange,
  onToggleGroupVisibility,
  onAnnotationClick,
  renderTotals,
}: {
  totals: { totalLinear: number; totalArea: number; totalCount: number };
  count: number;
  isActive: boolean;
  isHidden: boolean;
  readOnly: boolean;
  ownerType: TakeoffOwnerType;
  displayUnit: string;
  drilldownAnnotations: DrilldownAnnotation[];
  documentMap: Record<string, string>;
  onActiveGroupChange: (id: string | null) => void;
  onToggleGroupVisibility: (id: string) => void;
  onAnnotationClick: (params: { docId: string; pageNumber: number; annotationId: string }) => void;
  renderTotals: (t: { totalLinear: number; totalArea: number; totalCount: number }) => React.ReactNode;
}) {
  const byDoc = useMemo(
    () => aggregateByDocument(drilldownAnnotations, displayUnit, documentMap),
    [drilldownAnnotations, displayUnit, documentMap]
  );

  return (
    <Collapsible>
      <div
        className={cn(
          "rounded-md hover:bg-muted/50 cursor-pointer",
          isActive && "bg-primary/5 border border-primary/20"
        )}
      >
        <div
          className="flex items-start gap-2 p-2"
          onClick={() => !readOnly && onActiveGroupChange(null)}
        >
          <div className="h-3.5 w-3.5 rounded-full shrink-0 mt-0.5 bg-muted-foreground/30" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="text-xs font-medium text-muted-foreground">Unassigned</span>
              {count > 0 && (
                <Badge variant="secondary" className="text-[9px] px-1 py-0">{count}</Badge>
              )}
            </div>
            {renderTotals(totals)}
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {count > 0 && (
              <CollapsibleTrigger asChild>
                <button
                  className="p-0.5 rounded hover:bg-muted"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </button>
              </CollapsibleTrigger>
            )}
            <button
              className="p-0.5 rounded hover:bg-muted"
              onClick={(e) => { e.stopPropagation(); onToggleGroupVisibility("__unassigned__"); }}
            >
              {isHidden ? <EyeOff className="h-3 w-3 text-muted-foreground" /> : <Eye className="h-3 w-3 text-muted-foreground" />}
            </button>
          </div>
        </div>

        <CollapsibleContent>
          <div className="px-2 pb-2 space-y-1 border-t border-border/50 pt-1.5 ml-5">
            {byDoc.map((docSub) => (
              <Collapsible key={docSub.documentId}>
                <CollapsibleTrigger className="flex items-center gap-1.5 w-full px-1 py-1 rounded hover:bg-muted/30 text-left">
                  <FileText className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="text-[11px] truncate flex-1">{docSub.documentName}</span>
                  <Badge variant="secondary" className="text-[9px] px-1 py-0">{docSub.annotationCount}</Badge>
                  <ChevronDown className="h-2.5 w-2.5 text-muted-foreground" />
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-4 space-y-0.5">
                  {docSub.annotations.map((ann) => (
                    <DrilldownAnnotationRow
                      key={ann.id}
                      ann={ann}
                      ownerType={ownerType}
                      onAnnotationClick={onAnnotationClick}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

/* ─── Single annotation row ─── */

function DrilldownAnnotationRow({
  ann,
  ownerType,
  onAnnotationClick,
}: {
  ann: DrilldownAnnotation;
  ownerType: TakeoffOwnerType;
  onAnnotationClick: (params: { docId: string; pageNumber: number; annotationId: string }) => void;
}) {
  const valueStr = ann.measured_value != null ? `${ann.measured_value.toFixed(1)} ${ann.unit}` : "—";

  return (
    <button
      className="w-full flex items-center gap-1.5 px-1 py-1 rounded hover:bg-muted/50 text-left transition-colors group/ann"
      onClick={(e) => {
        e.stopPropagation();
        onAnnotationClick({
          docId: ann.document_id,
          pageNumber: ann.page_number,
          annotationId: ann.id,
        });
      }}
    >
      <div className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: ann.color }} />
      {annotationTypeIcon(ann.annotation_type)}
      <span className="text-[11px] truncate flex-1">
        {ann.label || ann.annotation_type.replace("measurement_", "")}
      </span>
      <span className="text-[10px] text-muted-foreground tabular-nums">{valueStr}</span>
      <span className="text-[10px] text-muted-foreground">p.{ann.page_number}</span>
      {ownerType === "project" && ann.source_estimate_annotation_id && (
        <Badge variant="outline" className="text-[8px] px-1 py-0 border-blue-300 text-blue-600">
          Est
        </Badge>
      )}
      <ArrowUpRight className="h-2.5 w-2.5 text-muted-foreground opacity-0 group-hover/ann:opacity-100 shrink-0" />
    </button>
  );
}
